// src/components/LoanAgainstProperty.js
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './LoanAgainstProperty.css';

const LoanAgainstProperty = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const heroSectionRef = useRef(null);

  useEffect(() => {
    // Cleanup function to prevent memory leaks
    return () => {
      // Remove any event listeners or cleanup animations if needed
    };
  }, []);

  const handleGoBack = () => {
    navigate('/home');
  };

  const handleApplyNow = (e) => {
    e.preventDefault();
    // Ensure button exists before handling click
    if (e.currentTarget) {
      console.log('Apply Now clicked');
      // Add your application logic here
      // Example: navigate('/apply-loan');
    }
  };

  const handleContactClick = (type, e) => {
    e.preventDefault();
    // Ensure button exists before handling click
    if (e.currentTarget) {
      switch(type) {
        case 'chat':
          console.log('Opening Live Chat');
          break;
        case 'email':
          console.log('Opening Email Form');
          break;
        case 'callback':
          console.log('Requesting Callback');
          break;
        default:
          break;
      }
    }
  };

  // Safe element access function
  const safeGetElement = (selector) => {
    if (typeof document !== 'undefined') {
      const element = document.querySelector(selector);
      return element || null;
    }
    return null;
  };

  return (
    <div className="lap-container" ref={containerRef}>
      {/* <button 
        className="lap-back-btn" 
        onClick={handleGoBack}
        aria-label="Go back to home page"
      >
        <span className="lap-back-arrow">←</span>
        <span className="lap-back-text">Back to Home</span>
        <div className="lap-back-hover-effect"></div>
      </button> */}

      <div className="lap-content">
        {/* Hero Section */}
        <div className="lap-hero-section" ref={heroSectionRef}>
          <div className="lap-pulse-ring" aria-hidden="true"></div>
          <div className="lap-icon-container">
            <span className="lap-property-icon" role="img" aria-label="Property">🏢</span>
          </div>
          <h1 className="lap-main-title">Loan Against Property</h1>
          <p className="lap-subtitle">Unlock the Value of Your Property for Financial Freedom</p>
        </div>

        {/* Overview Section */}
        <div className="lap-content-section lap-overview-section">
          <h2 className="lap-section-title">
            <span className="lap-section-icon" role="img" aria-label="Overview">📝</span>
            Overview of Loan Against Property (LAP)
          </h2>
          <div className="lap-section-content">
            <p className="lap-section-description">
              A <strong>secured loan</strong> where you pledge your owned property (residential, commercial, or industrial) as collateral to access funds. This financial instrument allows property owners to leverage their real estate assets while retaining ownership and use of the property.
            </p>
            
            <div className="lap-key-features">
              <h3>Key Features:</h3>
              <div className="lap-features-grid">
                <div className="lap-feature-card">
                  <div className="lap-feature-icon" role="img" aria-label="Purpose">🎯</div>
                  <h4>Purpose</h4>
                  <p>The loan amount can be used for a wide range of personal or business needs including business expansion, debt consolidation, education, medical expenses, or home renovation.</p>
                </div>
                <div className="lap-feature-card">
                  <div className="lap-feature-icon" role="img" aria-label="Loan Amount">💰</div>
                  <h4>Loan Amount & Tenure</h4>
                  <p>Typically ranges from <strong>₹10 lakh to ₹5 crore</strong>, with a flexible repayment period of <strong>5 to 20 years</strong>.</p>
                </div>
                <div className="lap-feature-card">
                  <div className="lap-feature-icon" role="img" aria-label="Interest Rates">📉</div>
                  <h4>Interest Rates</h4>
                  <p>Interest rates are generally lower than unsecured loans, usually starting from <strong>9.00% p.a.</strong>, ranging up to 13-15% p.a.</p>
                </div>
              </div>
            </div>

            <div className="lap-benefits-section">
              <h3>Key Benefits:</h3>
              <ul className="lap-benefits-list">
                <li><span className="lap-check-mark">✓</span> <strong>Lower Interest Rates:</strong> Compared to personal loans due to secured nature</li>
                <li><span className="lap-check-mark">✓</span> <strong>Higher Loan Amounts:</strong> Based on property value rather than income alone</li>
                <li><span className="lap-check-mark">✓</span> <strong>Longer Repayment Tenure:</strong> Reduces monthly EMI burden</li>
                <li><span className="lap-check-mark">✓</span> <strong>Credit Score Improvement:</strong> Timely repayment enhances credit history</li>
                <li><span className="lap-check-mark">✓</span> <strong>Tax Benefits:</strong> Interest may be tax-deductible if used for business purposes</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="lap-content-section lap-process-section">
          <h2 className="lap-section-title">
            <span className="lap-section-icon" role="img" aria-label="Process">⚙️</span>
            How Krishna Finconcept Services Presents the Process
          </h2>
          <div className="lap-section-content">
            <p className="lap-section-description">
              Our streamlined process makes obtaining a Loan Against Property simple and efficient. Here's how it works:
            </p>
            
            <div className="lap-process-steps">
              <div className="lap-process-step">
                <div className="lap-step-number">1</div>
                <div className="lap-step-content">
                  <h3>Check Eligibility</h3>
                  <p>You need to be an <strong>Indian citizen between 21-65 years old</strong>, with a stable income and a clear property title. Minimum income requirements vary by lender.</p>
                </div>
              </div>
              
              <div className="lap-process-step">
                <div className="lap-step-number">2</div>
                <div className="lap-step-content">
                  <h3>Submit Documents</h3>
                  <p>Required documents include:</p>
                  <ul>
                    <li><strong>Identity Proof:</strong> Aadhar Card, PAN Card, Passport</li>
                    <li><strong>Address Proof:</strong> Utility bills, Rental agreement</li>
                    <li><strong>Income Proof:</strong> Salary slips, IT Returns, Bank statements</li>
                    <li><strong>Property Documents:</strong> Title deed, NOC, Approved plan</li>
                  </ul>
                </div>
              </div>
              
              <div className="lap-process-step">
                <div className="lap-step-number">3</div>
                <div className="lap-step-content">
                  <h3>Property Valuation</h3>
                  <p>A lender-appointed valuer assesses the property's market value through physical inspection and market analysis. The loan amount is typically <strong>60-80% of the property's value</strong>.</p>
                </div>
              </div>
              
              <div className="lap-process-step">
                <div className="lap-step-number">4</div>
                <div className="lap-step-content">
                  <h3>Get Funds</h3>
                  <p>Once approved, the loan is disbursed directly to your account. The entire process usually takes <strong>7-15 working days</strong> from application to disbursement.</p>
                </div>
              </div>
            </div>
            
            <div className="lap-additional-info">
              <h4>Eligibility Criteria Details:</h4>
              <div className="lap-eligibility-grid">
                <div className="lap-eligibility-item">
                  <div className="lap-eligibility-icon" role="img" aria-label="Applicant">👤</div>
                  <div>
                    <h5>Applicant Profile</h5>
                    <p>Salaried & Self-Employed individuals, Business owners, Professionals</p>
                  </div>
                </div>
                <div className="lap-eligibility-item">
                  <div className="lap-eligibility-icon" role="img" aria-label="Income">📊</div>
                  <div>
                    <h5>Income Requirements</h5>
                    <p>Minimum annual income: ₹1.5-3 lakhs (varies by city and lender)</p>
                  </div>
                </div>
                <div className="lap-eligibility-item">
                  <div className="lap-eligibility-icon" role="img" aria-label="Property">🏠</div>
                  <div>
                    <h5>Property Eligibility</h5>
                    <p>Self-occupied, rented, residential, commercial, or industrial properties</p>
                  </div>
                </div>
                <div className="lap-eligibility-item">
                  <div className="lap-eligibility-icon" role="img" aria-label="Credit Score">⭐</div>
                  <div>
                    <h5>Credit Score</h5>
                    <p>Minimum 650+ for better terms and approval chances</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fees Section */}
        <div className="lap-content-section lap-fees-section">
          <h2 className="lap-section-title">
            <span className="lap-section-icon" role="img" aria-label="Fees">💰</span>
            Important Fees and Charges to Consider
          </h2>
          <div className="lap-section-content">
            <p className="lap-section-description">
              Understanding all associated costs helps in making an informed financial decision. Here are the typical charges:
            </p>
            
            <div className="lap-fees-table-container">
              <table className="lap-fees-table">
                <thead>
                  <tr>
                    <th>Particulars</th>
                    <th>Typical Charges</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Loan Processing Fees</strong></td>
                    <td>0.25% to 2% of Loan Amount</td>
                    <td>One-time fee for processing your application, usually deducted from the loan amount</td>
                  </tr>
                  <tr>
                    <td><strong>Penal Charges (Late Payment)</strong></td>
                    <td>Usually 2% per month</td>
                    <td>Applied on overdue amount for delayed EMI payments</td>
                  </tr>
                  <tr>
                    <td><strong>Prepayment / Foreclosure Charges</strong></td>
                    <td>Varies from Nil to 4%</td>
                    <td>Some lenders charge for early loan closure, while others offer free prepayment</td>
                  </tr>
                  <tr>
                    <td><strong>Stamp Duty & Legal Fees</strong></td>
                    <td>As per property value and state regulations</td>
                    <td>Legal verification and registration charges based on property location and value</td>
                  </tr>
                  <tr>
                    <td><strong>Technical Valuation Fees</strong></td>
                    <td>₹2,000 - ₹10,000</td>
                    <td>For property inspection and valuation by technical experts</td>
                  </tr>
                  <tr>
                    <td><strong>Documentation Charges</strong></td>
                    <td>₹1,000 - ₹5,000</td>
                    <td>For preparing loan agreement and legal documents</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="lap-fees-tips">
              <h4>
                <span role="img" aria-label="Tips">💡</span>
                Important Tips:
              </h4>
              <ul className="lap-tips-list">
                <li>Always ask for a detailed breakup of all charges before signing the agreement</li>
                <li>Compare processing fees and foreclosure charges across multiple lenders</li>
                <li>Negotiate processing fees - many lenders offer discounts or waiver</li>
                <li>Check if charges are inclusive or exclusive of GST</li>
                <li>Understand the difference between fixed and percentage-based charges</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Notes Section */}
        <div className="lap-content-section lap-notes-section">
          <h2 className="lap-section-title">
            <span className="lap-section-icon" role="img" aria-label="Warning">⚠️</span>
            Points to Keep in Mind
          </h2>
          <div className="lap-section-content">
            <div className="lap-notes-grid">
              <div className="lap-note-card lap-warning">
                <div className="lap-note-icon" role="img" aria-label="Intermediary">🔄</div>
                <h3>Intermediary Role</h3>
                <p>Krishna Finconcept Services is a facilitator. The final loan terms, approval, and disbursement come from partner banks or NBFCs (Non-Banking Financial Companies). We help you compare and choose the best option.</p>
              </div>
              
              <div className="lap-note-card lap-important">
                <div className="lap-note-icon" role="img" aria-label="Credit Impact">📈</div>
                <h3>Credit Impact</h3>
                <p>Successfully repaying the loan can improve your credit score, while defaulting risks the loss of your mortgaged property through legal proceedings.</p>
              </div>
              
              <div className="lap-note-card lap-tip">
                <div className="lap-note-icon" role="img" aria-label="Comparison">⚖️</div>
                <h3>Comparison is Key</h3>
                <p>Always compare offers, fees, interest rates, and terms from multiple lenders before committing. Consider both cost and service quality.</p>
              </div>
            </div>
            
            <div className="lap-final-cta">
              <h3>Ready to Unlock Your Property's Value?</h3>
              <p>Get personalized loan offers within 24 hours with minimal documentation.</p>
              <button 
                className="lap-apply-now-btn"
                onClick={handleApplyNow}
                aria-label="Apply for Loan Against Property"
              >
                Apply Now
                <span className="lap-btn-arrow">→</span>
              </button>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="lap-contact-section">
          <h3>Need Assistance?</h3>
          <p>Our loan experts are here to help you make the right decision</p>
          <a 
            href="tel:9909002220" 
            className="lap-contact-phone"
            aria-label="Call us at 9909002220"
          >
            <span role="img" aria-label="Phone">📞</span> 9909002220
          </a>
          <p className="lap-contact-timing">Mon-Sat: 9AM-9PM | Sun: 10AM-6PM</p>
          <div className="lap-contact-options">
            <button 
              className="lap-contact-btn"
              onClick={(e) => handleContactClick('chat', e)}
              aria-label="Open Live Chat"
            >
              <span className="lap-btn-icon" role="img" aria-label="Chat">💬</span>
              Live Chat
            </button>
            <button 
              className="lap-contact-btn"
              onClick={(e) => handleContactClick('email', e)}
              aria-label="Send Email Query"
            >
              <span className="lap-btn-icon" role="img" aria-label="Email">✉️</span>
              Email Query
            </button>
            <button 
              className="lap-contact-btn"
              onClick={(e) => handleContactClick('callback', e)}
              aria-label="Request Callback"
            >
              <span className="lap-btn-icon" role="img" aria-label="Callback">📱</span>
              Request Callback
            </button>
          </div>
        </div>
      </div>

      {/* Floating Elements */}
      <div className="lap-floating-element lap-el1" role="img" aria-hidden="true">🏢</div>
      <div className="lap-floating-element lap-el2" role="img" aria-hidden="true">🔑</div>
      <div className="lap-floating-element lap-el3" role="img" aria-hidden="true">💰</div>
      <div className="lap-floating-element lap-el4" role="img" aria-hidden="true">📈</div>
    </div>
  );
};

export default LoanAgainstProperty;